// Aba "Sugestões para juntar" (01/10/2026).
//
// Por que existe: com ~978 fornecedores, achar duplicado à mão na lista é
// garimpo. O servidor já aponta os prováveis (fornecedoresDuplicados.js); esta
// tela só mostra, deixa juntar pelo MESMO modal da aba Cadastro e deixa dizer
// "não são o mesmo" para a sugestão não voltar.
//
// A busca das sugestões fica no pai (FinanceiroFornecedores): o número ao lado
// do nome da aba precisa existir mesmo com a aba Cadastro aberta.
import { useState } from 'react';
import axios from 'axios';
import { Alert, Button, Card, Empty, Flex, Space, Spin, Table, Tag, Typography, App as AntApp } from 'antd';
import { MergeCellsOutlined, CloseCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { fmtDataBr } from '../../utils/dataFmt';
import { formatarCnpjCpf } from '../../utils/fornecedores';
import ModalMesclarFornecedores from './ModalMesclarFornecedores';
import {
  FornecedorLinha, SugestaoGrupo, Sugestoes, separarPorConfianca, fraseRodape,
  fmtDinheiro, mensagemDeErro, semCnpj,
} from './logica';

const { Text, Title } = Typography;

const COLUNAS: ColumnsType<FornecedorLinha> = [
  {
    title: 'Nome fantasia', dataIndex: 'nome_fantasia', key: 'nf',
    render: (v) => <Text strong style={{ color: '#020D7F' }}>{v || '—'}</Text>,
  },
  { title: 'Razão social', dataIndex: 'razao_social', key: 'rs', render: (v) => v || '—' },
  {
    title: 'CNPJ', key: 'cnpj', width: 150,
    // Mesma régua da aba Cadastro: pontuação solta não conta como CNPJ.
    render: (_v, r) => semCnpj(r)
      ? '—'
      : <Text style={{ fontFamily: 'monospace', fontSize: 12 }}>{formatarCnpjCpf(r.cnpj_cpf)}</Text>,
  },
  { title: 'Lançamentos', dataIndex: 'lancamentos', key: 'lanc', width: 100, align: 'right' },
  {
    // "lançado", não "pago": inclui o que ainda está em aberto no Contas a Pagar.
    title: 'Total lançado', dataIndex: 'total_pago', key: 'total', width: 130, align: 'right',
    render: (v: number) => v ? fmtDinheiro(v) : '—',
  },
  {
    title: 'Último lançamento', dataIndex: 'ultimo_lancamento', key: 'ult', width: 130,
    render: (v) => fmtDataBr(v) || '—',
  },
  {
    title: '', dataIndex: 'ativo', key: 'sit', width: 70,
    render: (v: boolean) => v ? null : <Tag>Inativo</Tag>,
  },
];

export default function SugestoesJuntar({
  dados, carregando, erro, podeExcluir, onTentarDeNovo, onMudou,
}: {
  dados: Sugestoes | null;
  carregando: boolean;
  erro: string | null;
  podeExcluir: boolean;
  /** Só recarrega as sugestões (botão do aviso de erro). */
  onTentarDeNovo: () => void;
  /** Depois de juntar/descartar/desfazer: recarrega as sugestões E a aba Cadastro. */
  onMudou: () => void;
}) {
  const { message } = AntApp.useApp();
  const [juntando, setJuntando] = useState<SugestaoGrupo | null>(null);
  // Um cartão por vez: evita duplo clique gravando o mesmo descarte duas vezes.
  const [ocupado, setOcupado] = useState<string | null>(null);

  async function chamar(rota: 'descartar' | 'desfazer', ids: string[]) {
    const token = await getToken();
    await axios.post(`${API_URL}/financeiro/fornecedores/sugestoes/${rota}`, { ids },
      { headers: { Authorization: token } });
  }

  async function desfazerDescarte(ids: string[], chave: string) {
    message.destroy(chave);
    try {
      await chamar('desfazer', ids);
      message.success('Pronto, a sugestão voltou.');
    } catch (e: any) {
      message.error('Não foi possível desfazer: ' + mensagemDeErro(e), 6);
    }
    onMudou();
  }

  async function descartar(g: SugestaoGrupo) {
    const ids = g.fornecedores.map(f => f.id);
    setOcupado(g.id);
    try {
      await chamar('descartar', ids);
      const chave = 'descarte-' + g.id;
      // O descarte é barato de errar (um clique no botão vizinho): o "Desfazer"
      // fica no próprio aviso, onde a pessoa está olhando, por 8 s.
      message.success({
        key: chave,
        duration: 8,
        content: (
          <span>
            Combinado, não vamos mais sugerir juntar estes cadastros.{' '}
            <Button type="link" size="small" style={{ padding: 0 }}
              onClick={() => desfazerDescarte(ids, chave)}>Desfazer</Button>
          </span>
        ),
      });
    } catch (e: any) {
      // 404 = algum cadastro já foi juntado/apagado por outra pessoa: a lista
      // na tela está velha, e recarregar é a correção.
      message.error('Não foi possível registrar: ' + mensagemDeErro(e), 6);
    } finally { setOcupado(null); }
    onMudou();
  }

  if (erro) {
    return (
      <div style={{ padding: '16px 24px' }}>
        <Alert type="error" showIcon
          message="Não foi possível carregar as sugestões" description={erro}
          action={<Button size="small" onClick={onTentarDeNovo}>Tentar de novo</Button>} />
      </div>
    );
  }

  if (!dados) {
    return <div style={{ padding: 48, textAlign: 'center' }}><Spin /></div>;
  }

  const { alta, media } = separarPorConfianca(dados.grupos);
  const rodape = fraseRodape(dados.nao_sugeridos);

  function cartao(g: SugestaoGrupo) {
    return (
      <Card key={g.id} size="small" style={{ marginBottom: 12 }}>
        <Flex justify="space-between" align="center" gap={8} wrap="wrap" style={{ marginBottom: 8 }}>
          <Text strong>{g.motivo_texto}</Text>
          {podeExcluir && (
            <Space>
              <Button size="small" icon={<CloseCircleOutlined />} loading={ocupado === g.id}
                disabled={ocupado !== null} onClick={() => descartar(g)}>
                Não são o mesmo
              </Button>
              <Button size="small" type="primary" danger icon={<MergeCellsOutlined />}
                disabled={ocupado !== null} onClick={() => setJuntando(g)}>
                Juntar…
              </Button>
            </Space>
          )}
        </Flex>
        <Table rowKey="id" columns={COLUNAS} dataSource={g.fornecedores} size="small"
          pagination={false} scroll={{ x: 'max-content' }} />
      </Card>
    );
  }

  function secao(titulo: string, explica: string, grupos: SugestaoGrupo[]) {
    if (!grupos.length) return null;
    return (
      <div style={{ marginBottom: 20 }}>
        <Title level={5} style={{ marginBottom: 2 }}>{titulo} ({grupos.length})</Title>
        <Text type="secondary" style={{ display: 'block', marginBottom: 10, fontSize: 12 }}>{explica}</Text>
        {grupos.map(cartao)}
      </div>
    );
  }

  return (
    <div style={{ padding: '16px 24px' }}>
      {!podeExcluir && dados.grupos.length > 0 && (
        <Alert type="info" showIcon style={{ marginBottom: 12 }}
          message="Só quem tem permissão para excluir fornecedores pode juntar ou descartar sugestões. Peça a essa pessoa, ou ao administrador de permissões." />
      )}

      <Spin spinning={carregando}>
        {dados.grupos.length === 0 && (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nenhuma sugestão agora." />
        )}
        {secao('Quase certeza',
          'Mesmo nome ou mesma razão social. Quase sempre é o mesmo cadastro repetido.', alta)}
        {secao('Confira antes de juntar',
          'Um nome parece o outro abreviado ou com algo a mais. Veja os lançamentos antes de decidir.', media)}
        {rodape && <Text type="secondary" style={{ fontSize: 12 }}>{rodape}</Text>}
      </Spin>

      {juntando && (
        <ModalMesclarFornecedores
          fornecedores={juntando.fornecedores}
          onFechar={() => setJuntando(null)}
          onSalvou={() => { setJuntando(null); onMudou(); }}
        />
      )}
    </div>
  );
}
